/**
 * Copyright (c) VVDN Technologies Pvt Ltd. All rights reserved.
 * Proprietary and confidential — custom algorithms.
 */

import type { FloorObstacle } from './types'

export type ObstaclePreset = {
  id: string
  label: string
  attenuationDb: number
  /** Footprint width on floor plan (m). */
  widthM: number
  /** Footprint depth on floor plan (m). */
  depthM: number
  /** Vertical height from floor (m) — used for 3D line-of-sight vs AP mount height. */
  heightM: number
}

export const OBSTACLE_PRESETS: ObstaclePreset[] = [
  { id: 'server_rack', label: 'Server rack', attenuationDb: 18, widthM: 0.6, depthM: 1.0, heightM: 2.0 },
  { id: 'metal_cabinet', label: 'Metal cabinet', attenuationDb: 16, widthM: 0.8, depthM: 0.6, heightM: 1.8 },
  { id: 'storage_shelf', label: 'Storage shelf', attenuationDb: 8, widthM: 1.2, depthM: 0.5, heightM: 2.0 },
  { id: 'machinery', label: 'Machinery', attenuationDb: 14, widthM: 2.0, depthM: 1.5, heightM: 2.5 },
  { id: 'elevator', label: 'Elevator shaft', attenuationDb: 22, widthM: 2.5, depthM: 2.5, heightM: 3.0 },
  { id: 'pillar', label: 'Pillar / column', attenuationDb: 20, widthM: 0.5, depthM: 0.5, heightM: 3.0 },
  { id: 'other', label: 'Other structure', attenuationDb: 10, widthM: 1.0, depthM: 1.0, heightM: 2.0 },
]

export type ObstacleDrawStyle = {
  fill: string
  stroke: string
  label: string
}

const OBSTACLE_DRAW_STYLES: Record<string, ObstacleDrawStyle> = {
  pillar: { fill: 'rgba(245,158,11,0.42)', stroke: '#fbbf24', label: '#fef3c7' },
  elevator: { fill: 'rgba(6,182,212,0.38)', stroke: '#22d3ee', label: '#cffafe' },
  server_rack: { fill: 'rgba(168,85,247,0.38)', stroke: '#c084fc', label: '#f3e8ff' },
  metal_cabinet: { fill: 'rgba(239,68,68,0.35)', stroke: '#f87171', label: '#fee2e2' },
  storage_shelf: { fill: 'rgba(234,179,8,0.32)', stroke: '#facc15', label: '#fef9c3' },
  machinery: { fill: 'rgba(249,115,22,0.38)', stroke: '#fb923c', label: '#ffedd5' },
  other: { fill: 'rgba(251,146,60,0.36)', stroke: '#fdba74', label: '#ffedd5' },
}

const DEFAULT_OBSTACLE_DRAW: ObstacleDrawStyle = {
  fill: 'rgba(251,146,60,0.36)',
  stroke: '#fb923c',
  label: '#ffedd5',
}

export function obstacleDrawStyle(type: string): ObstacleDrawStyle {
  return OBSTACLE_DRAW_STYLES[type] ?? DEFAULT_OBSTACLE_DRAW
}

export function obstaclePreset(type: string): ObstaclePreset {
  return OBSTACLE_PRESETS.find(p => p.id === type) ?? OBSTACLE_PRESETS[OBSTACLE_PRESETS.length - 1]
}

export function metersToFootprintPct(
  widthM: number,
  depthM: number,
  floorWidthM: number,
  floorHeightM: number,
): { w: number; h: number } {
  return {
    w: (widthM / Math.max(1, floorWidthM)) * 100,
    h: (depthM / Math.max(1, floorHeightM)) * 100,
  }
}

/** Resolve physical + canvas footprint (supports legacy obstacles with only w/h %). */
export function resolveObstacleFootprint(
  o: Pick<FloorObstacle, 'type' | 'w' | 'h' | 'widthM' | 'depthM' | 'heightM'>,
  floorWidthM: number,
  floorHeightM: number,
): { widthM: number; depthM: number; heightM: number; w: number; h: number } {
  const preset = obstaclePreset(o.type)
  const widthM = o.widthM ?? (o.w > 0 ? (o.w / 100) * floorWidthM : preset.widthM)
  const depthM = o.depthM ?? (o.h > 0 ? (o.h / 100) * floorHeightM : preset.depthM)
  const heightM = o.heightM ?? preset.heightM
  const { w, h } = metersToFootprintPct(widthM, depthM, floorWidthM, floorHeightM)
  return { widthM, depthM, heightM, w, h }
}

/** True when (x,y) lies inside any obstacle footprint — not a valid AP mount point. */
export function pointInObstacleFootprint(
  px: number,
  py: number,
  obstacles: FloorObstacle[],
  floorWidthM: number,
  floorHeightM: number,
): boolean {
  for (const o of obstacles) {
    const { w, h } = resolveObstacleFootprint(o, floorWidthM, floorHeightM)
    const rx = o.x - w / 2
    const ry = o.y - h / 2
    if (px >= rx && px <= rx + w && py >= ry && py <= ry + h) return true
  }
  return false
}

export function createObstacle(
  type: string,
  x: number,
  y: number,
  floorWidthM: number,
  floorHeightM: number,
  dims?: Partial<Pick<FloorObstacle, 'widthM' | 'depthM' | 'heightM'>>,
): FloorObstacle {
  const preset = obstaclePreset(type)
  const widthM = dims?.widthM ?? preset.widthM
  const depthM = dims?.depthM ?? preset.depthM
  const heightM = dims?.heightM ?? preset.heightM
  const { w, h } = metersToFootprintPct(widthM, depthM, floorWidthM, floorHeightM)
  return {
    id: '',
    type,
    x,
    y,
    w,
    h,
    widthM,
    depthM,
    heightM,
    attenuationDb: preset.attenuationDb,
  }
}

/** Closest point on segment AP→client as fraction t ∈ [0,1]. */
export function pathFractionToPoint(
  ax: number,
  ay: number,
  px: number,
  py: number,
  qx: number,
  qy: number,
): number {
  const dx = px - ax
  const dy = py - ay
  const lenSq = dx * dx + dy * dy
  if (lenSq < 1e-9) return 0
  let t = ((qx - ax) * dx + (qy - ay) * dy) / lenSq
  return Math.max(0, Math.min(1, t))
}

/** RF ray height (m) along AP→client path at fraction t. */
export function rayHeightAt(apHeightM: number, clientHeightM: number, t: number): number {
  return apHeightM + t * (clientHeightM - apHeightM)
}

/** Attenuation from vertical clearance (shared by walls and obstacles). */
export function verticalBarrierAttenuationDb(minRayH: number, barrierHeightM: number, baseDb: number): number {
  if (minRayH > barrierHeightM + 0.15) return 0
  if (minRayH >= barrierHeightM - 0.1) return baseDb * 0.35
  return baseDb
}

/**
 * Signal clears the obstacle when the ray passes above its top (e.g. AP at 4 m, rack 2 m).
 */
export function obstacleVerticalAttenuationDb(
  ax: number,
  ay: number,
  apHeightM: number,
  px: number,
  py: number,
  clientHeightM: number,
  obstacle: FloorObstacle,
  floorWidthM: number,
  floorHeightM: number,
): number {
  const { widthM, depthM, heightM, w, h } = resolveObstacleFootprint(obstacle, floorWidthM, floorHeightM)
  const rx = obstacle.x - w / 2
  const ry = obstacle.y - h / 2
  const intersects =
    segmentsIntersect(ax, ay, px, py, rx, ry, rx + w, ry) ||
    segmentsIntersect(ax, ay, px, py, rx + w, ry, rx + w, ry + h) ||
    segmentsIntersect(ax, ay, px, py, rx + w, ry + h, rx, ry + h) ||
    segmentsIntersect(ax, ay, px, py, rx, ry + h, rx, ry)

  if (!intersects) return 0

  const tCenter = pathFractionToPoint(ax, ay, px, py, obstacle.x, obstacle.y)
  const tCorners = [
    pathFractionToPoint(ax, ay, px, py, rx, ry),
    pathFractionToPoint(ax, ay, px, py, rx + w, ry),
    pathFractionToPoint(ax, ay, px, py, rx + w, ry + h),
    pathFractionToPoint(ax, ay, px, py, rx, ry + h),
  ]
  const samples = [tCenter, ...tCorners]
  let minRayH = Infinity
  for (const t of samples) {
    minRayH = Math.min(minRayH, rayHeightAt(apHeightM, clientHeightM, t))
  }

  const base = obstacle.attenuationDb ?? obstaclePreset(obstacle.type).attenuationDb
  return verticalBarrierAttenuationDb(minRayH, heightM, base)
}

function segmentsIntersect(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  x4: number,
  y4: number,
): boolean {
  const d = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4)
  if (Math.abs(d) < 1e-9) return false
  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / d
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / d
  return t >= 0 && t <= 1 && u >= 0 && u <= 1
}

export function formatObstacleSize(o: FloorObstacle, floorWidthM: number, floorHeightM: number): string {
  const { widthM, depthM, heightM } = resolveObstacleFootprint(o, floorWidthM, floorHeightM)
  return `${widthM.toFixed(1)}×${depthM.toFixed(1)} m · ${heightM.toFixed(1)} m tall`
}

/** Recompute canvas footprint % when floor dimensions change (keeps physical width/depth in meters). */
export function resyncObstacleFootprintsForFloor(
  obstacles: FloorObstacle[],
  floorWidthM: number,
  floorHeightM: number,
): FloorObstacle[] {
  return obstacles.map(o => {
    const { widthM, depthM, heightM, w, h } = resolveObstacleFootprint(o, floorWidthM, floorHeightM)
    return { ...o, widthM, depthM, heightM, w, h }
  })
}
